import { createContext, useContext, useState, useCallback, ReactNode } from "react";
import { useEssayChat } from "@/hooks/useEssayChat";

interface EssayChatContextType {
  isChatOpen: boolean;
  setIsChatOpen: (open: boolean) => void;
  messages: ReturnType<typeof useEssayChat>["messages"];
  isLoading: ReturnType<typeof useEssayChat>["isLoading"];
  chatId: ReturnType<typeof useEssayChat>["chatId"];
  contextAnalysisIds: ReturnType<typeof useEssayChat>["contextAnalysisIds"];
  savedChats: ReturnType<typeof useEssayChat>["savedChats"];
  sendMessage: ReturnType<typeof useEssayChat>["sendMessage"];
  cancelStream: ReturnType<typeof useEssayChat>["cancelStream"];
  addContextAnalysis: ReturnType<typeof useEssayChat>["addContextAnalysis"];
  removeContextAnalysis: ReturnType<typeof useEssayChat>["removeContextAnalysis"];
  startNewChat: ReturnType<typeof useEssayChat>["startNewChat"];
  deleteChat: ReturnType<typeof useEssayChat>["deleteChat"];
  saveImmediate: ReturnType<typeof useEssayChat>["saveImmediate"];
  loadChatById: ReturnType<typeof useEssayChat>["loadChatById"];
}

const EssayChatContext = createContext<EssayChatContextType | null>(null);

export function useEssayChatContext() {
  const ctx = useContext(EssayChatContext);
  if (!ctx) throw new Error("useEssayChatContext must be used within EssayChatProvider");
  return ctx;
}

export function useOptionalEssayChatContext() {
  return useContext(EssayChatContext);
}

export function EssayChatProvider({ children }: { children: ReactNode }) {
  const [isChatOpen, setIsChatOpen] = useState(false);
  const chat = useEssayChat();

  return (
    <EssayChatContext.Provider value={{ isChatOpen, setIsChatOpen, ...chat }}>
      {children}
    </EssayChatContext.Provider>
  );
}
